import { useState } from "react";
import { SettingSlider } from "../../../controls/SettingSlider";
import { useTranslate } from "@refinedev/core";
import { DEFAULT_MUSIC_VOLUME } from "../../../../constants";
import { Button, Flex, Typography } from "antd";
import { SpeakerHighIcon, SpeakerSlashIcon } from "@phosphor-icons/react";
import { useTemplatesStatusStore } from "../../../../store/useTemplatesStatusStore";
import { PanelSection } from "../../PanelSection";
import { useTemplateEditorStore } from "../../../../store/useTemplateEditorStore";
import { TemplateType } from "../../../../types";

export const Volume = () => {
  const t = useTranslate();
  const { currentChapter, music, setMusic, intro, closing } =
    useTemplateEditorStore((store) => ({
      currentChapter: store.currentChapter,
      music: store.templateState.music,
      setMusic: store.setMusic,
      intro: store.templateState.intro,
      closing: store.templateState.closing,
    }));

  const { addTemplateUpdate } = useTemplatesStatusStore((state) => ({
    addTemplateUpdate: state.addTemplateUpdate,
  }));
  const [dragValue, setDragValue] = useState<number | null>(null);

  const currentMusicVolume = music?.volume ?? DEFAULT_MUSIC_VOLUME;
  const currentValue = dragValue ?? Math.round(currentMusicVolume * 100);

  const title = t("components.templates.settings.musicTab.title", {
    chapterType: t(`components.templates.${currentChapter.type}`),
  });

  // Music is saved as volume 0 while the video's own sound plays (see generateTemplate)
  const isMutedByVideoSound =
    (currentChapter.type === TemplateType.INTRODUCTION &&
      intro.playVideoSound) ||
    (currentChapter.type === TemplateType.CLOSING && closing.playVideoSound);

  if (isMutedByVideoSound) {
    return (
      <PanelSection title={title}>
        <Typography.Text type="secondary">
          {t("components.templates.settings.musicTab.mutedByVideoSound")}
        </Typography.Text>
      </PanelSection>
    );
  }

  return (
    <PanelSection title={title}>
      <Flex align="center" gap={8} style={{ width: "100%" }}>
        <Button
          icon={null}
          onClick={() => {
            setMusic({ volume: currentValue ? 0 : DEFAULT_MUSIC_VOLUME });
            setDragValue(null);
            addTemplateUpdate();
          }}
          style={{ padding: 5 }}
        >
          {currentValue ? (
            <SpeakerHighIcon size={20} />
          ) : (
            <SpeakerSlashIcon size={20} />
          )}
        </Button>
        <SettingSlider
          label={t("components.templates.settings.musicTab.volume")}
          value={currentValue}
          onChange={(value) => {
            const roundedValue = Math.round(value);
            setDragValue(roundedValue);
          }}
          onChangeComplete={(value) => {
            const roundedValue = Math.round(value);
            setMusic({ volume: roundedValue / 100 });
            addTemplateUpdate();
            setDragValue(null);
          }}
          valueSuffix="%"
          style={{ flex: 1 }}
        />
      </Flex>
    </PanelSection>
  );
};
